/**
 * handlers/personal-facts.ts
 * Memory / personal facts: load, delete, toggle memory enabled, toggle pin.
 */

import { state } from '../state';
import { dom } from '../dom';
import {
  getMemorySnapshot    as apiGetMemorySnapshot,
  deleteMemoryFact     as apiDeleteMemoryFact,
  resetAllMemory       as apiResetAllMemory,
  setMemoryEnabled     as apiSetMemoryEnabled,
  toggleFactPin        as apiToggleFactPin,
} from '../api';
import { renderPersonalFactsList, showToast } from '../ui-renderers';

// ── Internal helper ───────────────────────────────────────────────────────────

/**
 * Flatten the grouped facts object returned by /api/user/memory into a flat
 * array enriched with factType, sorted pinned-first then by importanceScore.
 */
function _flattenMemoryFacts(factsObj: any): any[] {
  if (!factsObj || typeof factsObj !== 'object') return [];
  const flat: any[] = [];
  for (const [type, items] of Object.entries(factsObj)) {
    if (!Array.isArray(items)) continue;
    for (const f of items) {
      flat.push({ ...f, factType: type });
    }
  }
  return flat.sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    return (b.importanceScore ?? 0) - (a.importanceScore ?? 0);
  });
}

// ── Public API ────────────────────────────────────────────────────────────────

/** Load and render the user's memory snapshot (facts + metadata). */
export function loadPersonalFacts() {
  const list = dom.pfList;
  if (!list) return;
  list.innerHTML = '<div class="ai-panel-loading">Loading&hellip;</div>';

  apiGetMemorySnapshot()
    .then(snapshot => {
      const facts = _flattenMemoryFacts(snapshot.facts);
      state.personalFacts  = facts;
      state.memorySnapshot = snapshot;
      renderPersonalFactsList(facts, snapshot);
    })
    .catch(err => {
      console.warn('[AkiliBrain:memory]', err);
      if (list) list.innerHTML =
        '<div class="ai-panel-empty">Could not load memory. Check your connection.</div>';
    });
}

/**
 * Delete a single memory fact by ID, then reload the panel.
 */
export async function handleDeletePersonalFact(id: string) {
  try {
    await apiDeleteMemoryFact(id);
    state.personalFacts = state.personalFacts.filter((f: any) => f.id !== id);
    if (state.memorySnapshot?.facts) {
      for (const items of Object.values(state.memorySnapshot.facts)) {
        const typedItems = items as Array<{ id: string }>;
        const idx = typedItems.findIndex(f => f.id === id);
        if (idx !== -1) { typedItems.splice(idx, 1); break; }
      }
      state.memorySnapshot.factCount = Math.max(0, (state.memorySnapshot.factCount ?? 1) - 1);
    }
    renderPersonalFactsList(state.personalFacts, state.memorySnapshot);
    showToast('Fact removed.');
  } catch (err) {
    showToast('Could not remove fact.', 'error');
    console.warn('[AkiliBrain:memory]', err);
  }
}

/**
 * Delete ALL memory facts + preferences after confirmation.
 */
export async function handleDeleteAllPersonalFacts() {
  if (!window.confirm('Delete everything Kili remembers about you? This cannot be undone.')) return;
  try {
    await apiResetAllMemory();
    state.personalFacts  = [];
    state.memorySnapshot = null;
    renderPersonalFactsList([], null);
    showToast('Memory cleared — Kili will start fresh.');
  } catch (err) {
    showToast('Could not clear memory.', 'error');
    console.warn('[AkiliBrain:memory]', err);
  }
}

/**
 * Toggle memory recording on/off.
 */
export async function handleToggleMemoryEnabled(enabled: boolean) {
  try {
    const res = await apiSetMemoryEnabled(enabled);
    if (state.memorySnapshot) state.memorySnapshot.memoryEnabled = res.enabled;
    renderPersonalFactsList(state.personalFacts ?? [], state.memorySnapshot);
    showToast(res.message || (enabled ? 'Memory recording resumed.' : 'Memory recording paused.'));
  } catch (err) {
    showToast('Could not update memory setting.', 'error');
    console.warn('[AkiliBrain:memory]', err);
  }
}

/**
 * Toggle the pin state on a single fact.
 */
export async function handleToggleFactPin(factId: string, pinned: boolean) {
  try {
    await apiToggleFactPin(factId, pinned);
    const fact = state.personalFacts?.find((f: any) => f.id === factId);
    if (fact) fact.pinned = pinned;
    if (state.memorySnapshot?.facts) {
      for (const items of Object.values(state.memorySnapshot.facts)) {
        const typedItems = items as Array<{ id: string; pinned?: boolean }>;
        const f = typedItems.find(i => i.id === factId);
        if (f) { f.pinned = pinned; break; }
      }
    }
    state.personalFacts = _flattenMemoryFacts(state.memorySnapshot?.facts ?? {});
    renderPersonalFactsList(state.personalFacts, state.memorySnapshot);
    showToast(pinned ? 'Fact pinned — it will never decay.' : 'Fact unpinned.');
  } catch (err) {
    showToast('Could not update pin.', 'error');
    console.warn('[AkiliBrain:memory]', err);
  }
}
